import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import liff from "../../../lib/liff";

import Loading from "default/components/Loading";
import { COLORS } from "constants/colors";
import {
  getRequestFormChatCandidates,
  submitRequestFormChat,
  submitRequestFormOther,
} from "utils/api";
import AppLayout from "works/chi/components/AppLayout";
import StyledButton from "works/chi/components/StyledButton";

const CATEGORY_CHAT = "chat";
const CATEGORY_OTHER = "other";

function RequestForm() {
  const { t, i18n } = useTranslation(["chi"]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || i18n.language || "ja";

  const [category, setCategory] = useState(null);
  const [step, setStep] = useState(1);
  const [targetDate, setTargetDate] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [problemText, setProblemText] = useState("");
  const [inquiryText, setInquiryText] = useState("");
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const displayZone = lng === "en" ? "Europe/Warsaw" : "Asia/Tokyo";

  const formatDateTime = (isoString) => {
    const dt = DateTime.fromISO(isoString, { zone: "utc" })
      .setZone(displayZone)
      .setLocale(lng);
    return lng === "en"
      ? dt.toFormat("MMM d, yyyy HH:mm")
      : dt.toFormat("yyyy/MM/dd HH:mm");
  };

  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  const handleBackToMyPage = () => {
    navigate(withLng(`/chi/mypage?soul_id=${soulId}`));
  };

  const handleBackToChat = () => {
    liff.closeWindow();
  };

  const fetchCandidates = async () => {
    if (!soulId || !targetDate) {
      setIsError(true);
      return;
    }
    try {
      setIsError(false);
      setIsLoadingCandidates(true);
      const data = await getRequestFormChatCandidates(soulId, {
        date: targetDate,
        timezone: displayZone,
      });
      setCandidates(data.candidates || []);
      setSelectedCandidate(null);
      setStep(3);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const submitChat = async () => {
    if (!selectedCandidate || !problemText.trim()) {
      return;
    }
    try {
      setIsError(false);
      setIsProcessing(true);
      await submitRequestFormChat(soulId, {
        candidateId: selectedCandidate.candidate_id,
        problemText: problemText.trim(),
      });
      setIsCompleted(true);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const submitOther = async () => {
    if (!inquiryText.trim()) {
      return;
    }
    try {
      setIsError(false);
      setIsProcessing(true);
      await submitRequestFormOther(soulId, {
        inquiryText: inquiryText.trim(),
        language: lng,
      });
      setIsCompleted(true);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderTitle = () => {
    if (isCompleted) {
      return t("chi:requestForm.completedTitle");
    }
    if (!category) {
      return t("chi:requestForm.categoryTitle");
    }
    if (category === CATEGORY_OTHER) {
      return t("chi:requestForm.otherTitle");
    }
    if (step === 2) {
      return t("chi:requestForm.dateTitle");
    }
    if (step === 3) {
      return t("chi:requestForm.candidateTitle");
    }
    return t("chi:requestForm.problemTitle");
  };

  const renderCategoryStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <ButtonBase
        onClick={() => {
          setCategory(CATEGORY_CHAT);
          setStep(2);
        }}
        sx={categoryButtonSx}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: "bold", mb: 0.75 }}>
            {t("chi:requestForm.chatCategory")}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.7, opacity: 0.86 }}>
            {t("chi:requestForm.chatCategoryDescription")}
          </Typography>
        </Box>
      </ButtonBase>
      <ButtonBase
        onClick={() => {
          setCategory(CATEGORY_OTHER);
          setStep(1);
        }}
        sx={categoryButtonSx}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: "bold", mb: 0.75 }}>
            {t("chi:requestForm.otherCategory")}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.7, opacity: 0.86 }}>
            {t("chi:requestForm.otherCategoryDescription")}
          </Typography>
        </Box>
      </ButtonBase>
    </Box>
  );

  const renderDateStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        label={t("chi:requestForm.dateFieldLabel")}
        type="date"
        value={targetDate}
        onChange={(event) => setTargetDate(event.target.value)}
        fullWidth
        helperText={t("chi:requestForm.dateHelp")}
        inputProps={{ "aria-label": t("chi:requestForm.dateTitle") }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarTodayIcon
                sx={{ color: "rgba(255,255,255,0.86)", fontSize: 20 }}
              />
            </InputAdornment>
          ),
        }}
        sx={dateFieldSx}
      />
      {renderError()}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <StyledButton
          variant="contained"
          disabled={!targetDate || isLoadingCandidates}
          onClick={fetchCandidates}
        >
          {isLoadingCandidates
            ? t("chi:requestForm.loading")
            : t("chi:requestForm.next")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setCategory(null)}>
          {t("chi:requestForm.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderCandidate = (candidate) => {
    const selected = selectedCandidate?.candidate_id === candidate.candidate_id;
    const texts = candidate.texts && candidate.texts.length > 0
      ? candidate.texts
      : [candidate.text];
    return (
      <ButtonBase
        key={candidate.candidate_id}
        onClick={() => setSelectedCandidate(candidate)}
        sx={{
          ...candidateButtonSx,
          border: selected
            ? `2px solid ${COLORS.SKY_BLUE}`
            : "1px solid rgba(255,255,255,0.55)",
          background: selected ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.28)",
        }}
      >
        <Typography sx={{ fontSize: 12, lineHeight: 1.4, opacity: 0.82, mb: 1 }}>
          {formatDateTime(candidate.created_at)}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {texts.map((text, index) => (
            <Box
              key={`${candidate.candidate_id}-${index}`}
              sx={{
                alignSelf: "flex-start",
                maxWidth: "100%",
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                background: "rgba(255,255,255,0.12)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                  overflowWrap: "anywhere",
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </ButtonBase>
    );
  };

  const renderCandidateStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {isLoadingCandidates && <Loading />}
      {renderError()}
      {!isError && candidates.length === 0 && (
        <Typography sx={bodyTextSx}>{t("chi:requestForm.empty")}</Typography>
      )}
      {!isError && candidates.length > 0 && (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
          {candidates.map(renderCandidate)}
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <StyledButton
          variant="contained"
          disabled={!selectedCandidate}
          onClick={() => setStep(4)}
        >
          {t("chi:requestForm.next")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setStep(2)}>
          {t("chi:requestForm.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderProblemStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {selectedCandidate && renderCandidate(selectedCandidate)}
      <TextField
        value={problemText}
        onChange={(event) => setProblemText(event.target.value)}
        multiline
        minRows={5}
        placeholder={t("chi:requestForm.problemPlaceholder")}
        fullWidth
        inputProps={{
          "aria-label": t("chi:requestForm.problemTitle"),
          maxLength: 1000,
        }}
        sx={textFieldSx}
      />
      {renderError()}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <StyledButton
          variant="contained"
          disabled={!problemText.trim() || isProcessing}
          onClick={submitChat}
        >
          {isProcessing ? t("chi:requestForm.loading") : t("chi:requestForm.submit")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setStep(3)}>
          {t("chi:requestForm.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderOtherStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        value={inquiryText}
        onChange={(event) => setInquiryText(event.target.value)}
        multiline
        minRows={6}
        placeholder={t("chi:requestForm.otherPlaceholder")}
        fullWidth
        inputProps={{
          "aria-label": t("chi:requestForm.otherTitle"),
          maxLength: 2000,
        }}
        sx={textFieldSx}
      />
      {renderError()}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <StyledButton
          variant="contained"
          disabled={!inquiryText.trim() || isProcessing}
          onClick={submitOther}
        >
          {isProcessing ? t("chi:requestForm.loading") : t("chi:requestForm.submit")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setCategory(null)}>
          {t("chi:requestForm.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderCompletedStep = () => (
    <Typography sx={bodyTextSx}>{t("chi:requestForm.completedDescription")}</Typography>
  );

  const renderError = () =>
    isError ? (
      <Typography sx={bodyTextSx}>{t("chi:requestForm.error")}</Typography>
    ) : null;

  return (
    <AppLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          py: 4,
          gap: 3,
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 24,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("chi:requestForm.title")}
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 17,
            fontWeight: "bold",
            lineHeight: 1.7,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {renderTitle()}
        </Typography>

        {isCompleted && renderCompletedStep()}
        {!isCompleted && !category && renderCategoryStep()}
        {!isCompleted && category === CATEGORY_CHAT && step === 2 && renderDateStep()}
        {!isCompleted && category === CATEGORY_CHAT && step === 3 && renderCandidateStep()}
        {!isCompleted && category === CATEGORY_CHAT && step === 4 && renderProblemStep()}
        {!isCompleted && category === CATEGORY_OTHER && renderOtherStep()}

        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 1 }}>
          <StyledButton variant="contained" onClick={handleBackToMyPage}>
            {t("chi:requestForm.back")}
          </StyledButton>
          <StyledButton variant="contained" onClick={handleBackToChat}>
            {t("chi:requestForm.backToChat")}
          </StyledButton>
        </Box>
      </Box>
    </AppLayout>
  );
}

const bodyTextSx = {
  color: COLORS.TEXT_WHITE,
  fontSize: 14,
  lineHeight: 1.7,
  textAlign: "center",
  whiteSpace: "pre-line",
};

const categoryButtonSx = {
  width: "100%",
  display: "block",
  textAlign: "left",
  border: "1px solid rgba(255,255,255,0.55)",
  borderRadius: "6px",
  px: 2,
  py: 1.5,
  background: "rgba(0,0,0,0.28)",
  color: COLORS.TEXT_WHITE,
};

const candidateButtonSx = {
  width: "100%",
  display: "block",
  textAlign: "left",
  borderRadius: "6px",
  px: 2,
  py: 1.5,
  color: COLORS.TEXT_WHITE,
};

const textFieldSx = {
  "& .MuiInputBase-root": {
    color: COLORS.TEXT_WHITE,
    background: "rgba(0,0,0,0.28)",
    borderRadius: "6px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.55)",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(255,255,255,0.58)",
    opacity: 1,
  },
  "& input::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
  },
};

const dateFieldSx = {
  ...textFieldSx,
  "& .MuiInputBase-root": {
    ...textFieldSx["& .MuiInputBase-root"],
    minHeight: 58,
    background: "rgba(255,255,255,0.12)",
    cursor: "pointer",
  },
  "& .MuiInputBase-input": {
    cursor: "pointer",
    fontSize: 17,
    fontWeight: "bold",
    letterSpacing: 0,
    py: 1.55,
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.82)",
    fontSize: 15,
    fontWeight: "bold",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.TEXT_WHITE,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.82)",
    borderWidth: 1.5,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.TEXT_WHITE,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.SKY_BLUE,
    borderWidth: 2,
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.72)",
    mx: 0,
    mt: 1,
    fontSize: 12,
    lineHeight: 1.6,
  },
  "& input::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
    opacity: 0.95,
    cursor: "pointer",
  },
};

export default RequestForm;
